import { fetchQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { IntegrationsPortErrors } from 'modules/Integrations/domain'
import type { NetsuiteIntegrationConfig } from './netsuite.integration.config'
import { findLiveCustomerPushToggle } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'

export const netsuiteConfigurationLoader = async (): Promise<{
  data: NetsuiteIntegrationConfig | null
  error: IntegrationsPortErrors | null
}> => {
  // Kicked off before the integration read so the two load in parallel. The
  // client resolves errors as data, so an early return never leaks a rejection.
  const togglesRequest = dashboardv99990101Client.getEntityTypeSyncToggles()

  let integrationResponse
  try {
    integrationResponse = await fetchQuery(
      apiQueryClient,
      dashboard20240730Client.getIntegration,
      'NetSuite',
      { staleTime: 1 * MINUTES }
    )
  } catch {
    return {
      data: null,
      error: IntegrationsPortErrors.Other
    }
  }

  // A failed toggles read leaves auto-linking off rather than erroring the
  // whole load, so the connection and settings still render.
  const toggles = await togglesRequest
  const autoLinkToggle = findLiveCustomerPushToggle(
    toggles.data?.items ?? [],
    'NetSuite'
  )

  const integration =
    integrationResponse?.service === 'NetSuite' ? integrationResponse : null
  const connection = integration?.connection ?? null
  const settings = integration?.settings ?? null

  return {
    data: {
      netsuiteAccountId: connection?.netsuiteAccountId ?? '',
      consumerKey: connection?.consumerKey ?? '',
      certificateId: connection?.certificateId ?? '',
      // privateKey is write-only: never returned, so always blank on load.
      privateKey: '',
      subsidiaryId: settings?.subsidiaryId ?? '',
      defaultItemIncomeAccountId: settings?.defaultItemIncomeAccountId ?? '',
      defaultTaxScheduleId: settings?.defaultTaxScheduleId ?? '',
      prepaidCreditItemId: settings?.prepaidCreditItemId ?? '',
      commitmentItemId: settings?.commitmentItemId ?? '',
      creditGrantItemId: settings?.creditGrantItemId ?? '',
      salesTaxItemId: settings?.salesTaxItemId ?? '',
      deferredRevenueItemId: settings?.deferredRevenueItemId ?? '',
      isConnected: integration?.state === 'CONNECTED',
      autoLinkCustomers: autoLinkToggle?.enabled ?? false,
      autoLinkSyncPermission: autoLinkToggle?.syncPermission
    },
    error: null
  }
}
